import { useEffect, useState, useMemo } from "react";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";

export default function FaqPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [openIdx, setOpenIdx] = useState(0);

  useEffect(() => {
    api.get("/faq").then((r) => setItems(r.data));
  }, []);

  const categories = useMemo(() => {
    const set = new Set(items.map((i) => i.category));
    return ["All", ...Array.from(set)];
  }, [items]);

  const filtered = items.filter((i) => {
    const hit = i.q.toLowerCase().includes(q.toLowerCase()) || i.a.toLowerCase().includes(q.toLowerCase());
    return hit && (cat === "All" || i.category === cat);
  });

  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((i) => ({ "@type": "Question", name: i.q, acceptedAnswer: { "@type": "Answer", text: i.a } })),
  };

  return (
    <>
      <SEO title="FAQ" description="Frequently asked questions about selling gift cards on GC SPARK." schema={schema} />

      {/* Search-style hero from theme */}
      <section className="search-home-hero-section" data-testid="faq-hero">
        <div className="main-container w-container">
          <div className="search-heading" style={{ textAlign: "center", maxWidth: 780, margin: "0 auto" }}>
            <div style={{ display: "inline-block", padding: "6px 14px", borderRadius: 90, background: "#eaf0ff", color: "#346af8", fontSize: 12, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 18 }}>
              Help center
            </div>
            <h1 className="h1 _700-bold">Answers, straight from <span style={{ color: "#346af8" }}>the ops team</span></h1>
            <p className="main-body-text _500-medium" style={{ marginTop: 18, color: "#4a4d5f" }}>
              Everything about selling gift cards, payouts, and verification — written by the people who actually do it.
            </p>

            {/* Search input */}
            <div style={{ position: "relative", maxWidth: 560, margin: "34px auto 0" }}>
              <img src="/jumbo/images/Group.svg" alt="" style={{ position: "absolute", left: 22, top: "50%", transform: "translateY(-50%)", width: 18, opacity: 0.6 }} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search questions…"
                data-testid="faq-search"
                style={{ width: "100%", height: 58, paddingLeft: 56, paddingRight: 22, borderRadius: 90, border: "1px solid #d8dcec", background: "#fff", boxShadow: "0 20px 40px -24px rgba(41,45,85,.2)", fontSize: 16, outline: "none", color: "#0b0d17" }}
              />
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", marginTop: 22 }}>
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  data-testid={`faq-cat-${c.replace(/\s/g, "-").toLowerCase()}`}
                  style={{
                    padding: "10px 20px", borderRadius: 90, fontSize: 13.5, fontWeight: 700, cursor: "pointer",
                    border: cat === c ? "1px solid #0b0d17" : "1px solid #d8dcec",
                    background: cat === c ? "#0b0d17" : "#fff",
                    color: cat === c ? "#fff" : "#0b0d17",
                    transition: "all .2s",
                  }}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ accordion using theme's faq-section */}
      <section className="faq-section" style={{ paddingTop: 60 }} data-testid="faq-list">
        <div className="main-container">
          <div className="faq-wrapper">
            <div className="faq-accordion-wrapper">
              <div className="faq-accordion-container">
                <div className="c-faq">
                  {filtered.map((i, idx) => {
                    const open = openIdx === idx;
                    return (
                      <div className="c-faq-item" key={i.q} data-testid={`faq-item-${idx}`}>
                        <div
                          className="c-faq-q"
                          role="button"
                          tabIndex={0}
                          aria-expanded={open}
                          onClick={() => setOpenIdx(open ? -1 : idx)}
                          onKeyDown={(e) => e.key === "Enter" && setOpenIdx(open ? -1 : idx)}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="c-faq-q-text">
                            <h5 className="h6 _500-medium">{i.q}</h5>
                          </div>
                          <div className="c-faq-icon">
                            <div className="boss-faq-toggle">{open ? "−" : "+"}</div>
                          </div>
                        </div>
                        {open && (
                          <div className="c-faq-a" style={{ height: "auto" }}>
                            <div className="c-faq-divider"><div className="faq-divider" /></div>
                            <div className="c-faq-a-text">
                              <p className="main-body-text-variant-2 _500-medium" style={{ whiteSpace: "pre-wrap" }}>{i.a}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {filtered.length === 0 && (
                    <div style={{ textAlign: "center", padding: 60, color: "#9a93a6" }}>
                      No results for "{q}". Try a different word.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand independence disclaimer */}
      <section className="changelog-section" data-testid="faq-brand-relationships" style={{ paddingTop: 40, paddingBottom: 40 }}>
        <div className="main-container w-container">
          <div className="changelog-blocks-container">
            <div className="changelog-heading-container">
              <h2 className="h3 _700-bold">Brand relationships</h2>
            </div>
            <div className="template-block" style={{ padding: 32, borderRadius: 20, background: "#fff", border: "1px solid #eaeaf0", marginTop: 20 }}>
              <p className="changelog-para" style={{ lineHeight: 1.7, color: "#4a4d5f", margin: 0 }}>
                GC SPARK accepts gift cards issued by various third-party brands. These brands do not operate GC SPARK and are not responsible for our services. GC SPARK independently determines its purchase rates, verification process, and payouts. Brand names and logos are displayed solely to identify the gift cards accepted on the platform.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Still stuck CTA */}
      <section className="boss-section" data-testid="faq-cta">
        <div className="boss-container">
          <div className="boss-cta-band">
            <h2>Still can't find the answer?</h2>
            <p>Email the ops team — average reply time is under 6 hours on business days.</p>
            <Link to="/contact" className="boss-btn" data-testid="faq-cta-contact">Contact us</Link>
          </div>
        </div>
      </section>
    </>
  );
}
